import { readClarityProjectId, startClarity } from "@/lib/analytics/clarity";
import { readCookieConsent } from "@/lib/legal/cookie-consent";
import { announcePageTransition, type PageNavigationType } from "@/lib/motion/page-transition";

/**
 * Runs before hydration. Whoever accepted on a past visit gets Clarity here instead of after the
 * app is interactive, which is what keeps short visits and the start of each page in the
 * recording. Whoever accepts now is started by `ClarityContainer`, which listens to the banner.
 *
 * Pending is not consent: `readCookieConsent` returns `"aceito"` only for a stored yes.
 * Wrapped because a failure here must never keep the app from starting.
 */
try {
  const projectId = readClarityProjectId();
  if (projectId && readCookieConsent() === "aceito") startClarity(projectId);
} catch {
  // Clarity simply starts later, when the container mounts.
}

/**
 * Next calls this when a client navigation is dispatched — the only global signal the App Router
 * gives for it. It feeds the page loader (`components/shared/page-loader.tsx`), and says whether
 * the navigation moves through history: going back returns to where the page was left.
 *
 * Wrapped because this runs inside the router's dispatch: an indicator must never be the reason a
 * navigation fails.
 */
export function onRouterTransitionStart(url: string, navigationType: PageNavigationType): void {
  try {
    announcePageTransition(url, navigationType);
  } catch {
    // The page simply navigates without the veil.
  }
}
